import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Button } from '../primitives/Button.tsx';
import { Icon } from '../primitives/Icon.tsx';

/**
 * PortalShell - one shell for booking, the preference centre and the customer
 * portal.
 *
 * Not three shells with shared styling: three would be three places for the
 * navigation rail to leak in, and the navigation rail is where the workspace's
 * own people, pipelines and settings live. What differs between the surfaces
 * is the content passed in, and nothing else.
 *
 * Four things it refuses to do.
 *
 * **No internal chrome, ever.** No rail, no command palette, no workspace
 * switcher, no "signed in as". There is no signed-in person here: the visitor
 * is a customer holding a link.
 *
 * **It renders what it is given and looks nothing up.** Every field arrives
 * already reduced by the server's allowlist. A shell that fetched would be a
 * shell that could fetch something it should not, and the filter would be in
 * the browser where a `curl` goes around it.
 *
 * **A failed link gets a page, not an error.** Three refusals, three pages: an
 * expired link offers a new one, a revoked link does not, an invalid link
 * acknowledges nothing - not even that this workspace exists.
 *
 * **The workspace's name is the only branding.** No plan, no user count, no
 * owner: those are facts about the customer's supplier that the customer has
 * no business reading off a booking page.
 */

export type PortalSurfaceKind = 'booking' | 'preferences' | 'portal' | 'partner' | 'consent';

export interface PortalRefusalView {
  kind: 'invalid' | 'expired' | 'revoked' | 'wrong-surface';
  canRequestNewLink: boolean;
}

export interface PortalShellLabels {
  /** The workspace's name is interpolated by the caller, never built here. */
  poweredBy: string;
  /** One per refusal. Three pages, because three different things happened. */
  refusal: Record<PortalRefusalView['kind'], { title: string; body: string }>;
  requestNewLink: string;
  /** Announced on the region holding the surface's own content. */
  content: string;
}

export interface PortalShellProps {
  surface: PortalSurfaceKind;
  /** Null while loading, and when the link did not work. */
  workspaceName: string | null;
  refusal?: PortalRefusalView | null;
  onRequestNewLink?: (() => void) | undefined;
  labels: PortalShellLabels;
  children: ReactNode;
}

export function PortalShell({
  surface,
  workspaceName,
  refusal,
  onRequestNewLink,
  labels,
  children,
  ...rest
}: PortalShellProps & Record<string, unknown>): JSX.Element {
  const failed = refusal ?? null;

  return (
    <div
      {...passthrough(rest)}
      data-component="PortalShell"
      data-portal-surface={surface}
      className="flex min-h-screen w-full flex-col items-center bg-canvas px-gutter py-6"
    >
      <div className="flex w-full max-w-reading flex-col gap-4">
        {/*
         * The header. A name and nothing else.
         *
         * Deliberately not a RecordHeader, not a nav rail, not a topbar: those
         * carry a workspace switcher and a signed-in person, and there is
         * neither here.
         */}
        {/* No name, no heading: an empty h1 is announced as a heading with nothing
            in it. A page that cannot yet say whose it is - the consent
            confirmation, before its token is checked - gives its own title. */}
        {workspaceName ? (
          <header data-portal-header className="flex items-baseline gap-2">
            <h1 className="serif-moment text-h2 text-ink">{workspaceName}</h1>
          </header>
        ) : null}

        {failed ? (
          <section
            role="alert"
            data-portal-refusal={failed.kind}
            className="flex flex-col gap-2 rounded-card border border-rule bg-surface p-4"
          >
            <span className="flex items-center gap-2 text-lead text-ink">
              <Icon name="LinkBreak" size="inline" decorative />
              {labels.refusal[failed.kind].title}
            </span>
            <p className="text-body text-ink-meta">{labels.refusal[failed.kind].body}</p>
            {/*
             * The offer, and only when it makes sense.
             *
             * An expired unsubscribe link that offered nothing is how a spam
             * complaint happens instead of an opt-out. A revoked link offers
             * nothing, because somebody took it away on purpose - re-issuing
             * it from the page it revoked would make revocation meaningless.
             */}
            {failed.canRequestNewLink && onRequestNewLink ? (
              <span>
                <Button intent="primary" data-request-new-link onClick={onRequestNewLink}>
                  {labels.requestNewLink}
                </Button>
              </span>
            ) : null}
          </section>
        ) : (
          <main
            aria-label={labels.content}
            data-portal-content
            className={cx('flex flex-col gap-4 rounded-card border border-rule bg-surface p-4')}
          >
            {children}
          </main>
        )}

        {/* No footer when there is nobody to name. A refusal page must not
            confirm that a workspace exists at this address. */}
        {labels.poweredBy ? (
          <footer data-portal-footer className="text-meta text-ink-meta">
            {labels.poweredBy}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A field, rendered from a key and an already-reduced value.
 *
 * The label comes from the caller: the shell has no message catalogue, and a
 * key like `full_name` is a column name rather than a word anybody says.
 */
export function PortalField({
  label,
  value,
  ...rest
}: { label: string; value: string | null } & Record<string, unknown>): JSX.Element {
  return (
    <div {...passthrough(rest)} data-component="PortalField" className="flex flex-col gap-0.5">
      <span className="text-micro uppercase tracking-label text-ink-label">{label}</span>
      <span className="text-body text-ink">{value ?? ''}</span>
    </div>
  );
}
